import React, { useCallback, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { API_BASE_URL } from "../../utils/constants";
import { readErrorMessage } from "../../utils/apiError";
import BankFormModal from "./BankFormModal";
import ProductFormModal from "./ProductFormModal";

// Same look as the Contested Cases screen: compact striped tables, pill badges, small action buttons
const TABLE_STYLE = { fontSize: "0.82rem" };
const BANK_ACTIONS_WIDTH = "300px";
const PRODUCT_ACTIONS_WIDTH = "240px";

const Pill = ({ color, children }) => (
  <span
    style={{
      background: color,
      color: "#fff",
      padding: "2px 8px",
      borderRadius: "10px",
      fontSize: "0.75rem",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

const StatusPill = ({ status }) =>
  status === 0 ? <Pill color="#6c757d">Inactive</Pill> : <Pill color="#198754">Active</Pill>;

const FormatPill = ({ product }) => {
  if (!product.isConfigured) return <Pill color="#fd7e14">Columns not set up</Pill>;
  return product.detailType === "VEHICLE" ? <Pill color="#6f42c1">Vehicle</Pill> : <Pill color="#0d6efd">Loan</Pill>;
};

const ActionButton = ({ variant = "secondary", onClick, disabled, children }) => (
  <button
    type="button"
    className={`btn btn-sm btn-outline-${variant}`}
    style={{ fontSize: "0.75rem", padding: "2px 8px", whiteSpace: "nowrap" }}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    disabled={disabled}
  >
    {children}
  </button>
);

// Document formats: documents are made only from the uploaded Word files, so missing ones are a warning
const WordStatus = ({ count, needs }) => {
  if (!count) return <Pill color="#dc3545">No Word files – documents can't be made</Pill>;
  if (count < needs) return <Pill color="#fd7e14">Documents {count} of {needs}</Pill>;
  return <Pill color="#198754">Documents {count} of {needs}</Pill>;
};

const toastError = (message) => toast.error(message, { theme: "colored", autoClose: 4000 });
const toastSuccess = (message) => toast.success(message, { theme: "colored", autoClose: 2000 });

// Banks & Products: add banks, their products and each product's Excel column setup.
// Deactivate hides a bank / product from Upload LOT; Delete is only allowed when no case uses it.
const BanksProducts = () => {
  const [banks, setBanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [uploadFields, setUploadFields] = useState(null);
  const [wordSummary, setWordSummary] = useState(null);
  const [selectedBankId, setSelectedBankId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [bankModal, setBankModal] = useState({ show: false, bank: null });
  const [productModal, setProductModal] = useState({ show: false, product: null });

  const loadData = useCallback(async () => {
    try {
      const [bankRes, productRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/Client`),
        fetch(`${API_BASE_URL}/api/products`),
      ]);
      if (!bankRes.ok) throw new Error(await readErrorMessage(bankRes));
      if (!productRes.ok) throw new Error(await readErrorMessage(productRes));
      setBanks(await bankRes.json());
      setProducts(await productRes.json());
      const summaryRes = await fetch(`${API_BASE_URL}/api/doc-templates/summary`);
      if (summaryRes.ok) setWordSummary(await summaryRes.json());
    } catch (e) {
      toastError(`Could not load banks: ${e.message}`);
    }
  }, []);

  useEffect(() => {
    loadData();
    fetch(`${API_BASE_URL}/api/products/upload-fields`)
      .then((res) => res.json())
      .then(setUploadFields)
      .catch(() => toastError("Could not load the list of upload fields"));
  }, [loadData]);

  const selectedBank = banks.find((b) => b.Client_id === selectedBankId);
  const bankProducts = products.filter((p) => p.Client_id === selectedBankId);
  const productCount = (clientId) => products.filter((p) => p.Client_id === clientId).length;
  const wordCount = (clientId, productId = null) =>
    wordSummary?.counts.find((c) => c.clientId === clientId && (c.productId ?? null) === productId)?.count || 0;
  const search = searchTerm.trim().toLowerCase();
  const filteredBanks = banks.filter(
    (b) => !search || b.client_name.toLowerCase().includes(search) || (b.Add1 || "").toLowerCase().includes(search),
  );

  const setStatus = async (url, active, label) => {
    const action = active ? "Activate" : "Deactivate";
    const note = active ? "" : " It will no longer appear in Upload LOT. Existing cases are not affected.";
    if (!window.confirm(`${action} ${label}?${note}`)) return;
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      toastSuccess(`${label} ${active ? "activated" : "deactivated"}`);
      await loadData();
    } catch (e) {
      toastError(e.message);
    }
  };

  // The server refuses when cases use the bank / product; its message says to deactivate instead
  const handleDelete = async (url, label, note) => {
    if (!window.confirm(`Delete ${label} permanently?${note}`)) return false;
    try {
      const res = await fetch(url, { method: "DELETE" });
      if (!res.ok) throw new Error(await readErrorMessage(res));
      toastSuccess(`${label} deleted`);
      await loadData();
      return true;
    } catch (e) {
      toastError(e.message);
      return false;
    }
  };

  // Full product (with columns) is needed to edit it; the list endpoint only has the summary
  const openProduct = async (product) => {
    if (!product) {
      setProductModal({ show: true, product: null });
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/${product.Product_id}`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      setProductModal({ show: true, product: await res.json() });
    } catch (e) {
      toastError(e.message);
    }
  };

  return (
    <div className="container-fluid px-3">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div>
          <h5 className="mb-1">Banks &amp; Products</h5>
          <small className="text-muted">Banks, their products and each product's Excel columns for Upload LOT</small>
        </div>
        <button
          className="custBtn"
          style={{ fontSize: "0.8rem", padding: "4px 12px" }}
          onClick={() => setBankModal({ show: true, bank: null })}
        >
          + Add Bank
        </button>
      </div>

      {/* ── Banks ──────────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
        <input
          type="text"
          placeholder="Search bank name or address…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-control"
          style={{ maxWidth: "320px", fontSize: "0.8rem" }}
        />
        <small className="text-muted">Click a bank to see its products</small>
      </div>

      <div className="table-responsive">
        <table className="table table-striped table-bordered table-hover table-sm text-center" style={TABLE_STYLE}>
          <thead>
            <tr>
              <th style={{ width: "50px" }}>Sr No</th>
              <th style={{ textAlign: "left" }}>Bank</th>
              <th style={{ textAlign: "left" }}>Branch / corporate office</th>
              <th style={{ width: "80px" }}>Products</th>
              <th style={{ width: "170px" }}>Documents</th>
              <th style={{ width: "90px" }}>Status</th>
              <th style={{ width: BANK_ACTIONS_WIDTH }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBanks.map((bank, index) => {
              const isSelected = bank.Client_id === selectedBankId;
              return (
                <tr
                  key={bank.Client_id}
                  onClick={() => setSelectedBankId(isSelected ? null : bank.Client_id)}
                  className={isSelected ? "table-warning" : ""}
                  style={{ cursor: "pointer", verticalAlign: "middle" }}
                >
                  <td>{index + 1}</td>
                  <td className="fw-semibold" style={{ textAlign: "left", whiteSpace: "nowrap" }}>
                    {bank.client_name}
                  </td>
                  <td
                    title={bank.Add1 || ""}
                    style={{
                      textAlign: "left",
                      maxWidth: "420px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {bank.Add1 || "—"}
                  </td>
                  <td>{productCount(bank.Client_id)}</td>
                  <td>{wordSummary && <WordStatus count={wordCount(bank.Client_id)} needs={wordSummary.bankNeeds} />}</td>
                  <td>
                    <StatusPill status={bank.Status} />
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-1">
                      <ActionButton variant="primary" onClick={() => setSelectedBankId(bank.Client_id)}>
                        Products
                      </ActionButton>
                      <ActionButton onClick={() => setBankModal({ show: true, bank })}>Edit</ActionButton>
                      <ActionButton
                        variant={bank.Status === 0 ? "success" : "warning"}
                        onClick={() =>
                          setStatus(
                            `${API_BASE_URL}/api/Client/${bank.Client_id}/status`,
                            bank.Status === 0,
                            bank.client_name,
                          )
                        }
                      >
                        {bank.Status === 0 ? "Activate" : "Deactivate"}
                      </ActionButton>
                      <ActionButton
                        variant="danger"
                        onClick={async () => {
                          const deleted = await handleDelete(
                            `${API_BASE_URL}/api/Client/${bank.Client_id}`,
                            bank.client_name,
                            " Its products are deleted too. Only possible if no cases use this bank.",
                          );
                          if (deleted && isSelected) setSelectedBankId(null);
                        }}
                      >
                        Delete
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredBanks.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-muted py-3">
                  {search ? "No results found" : "No banks yet"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Products of the selected bank ──────────────────────────────────── */}
      {selectedBank && (
        <div className="mt-3">
          <div
            className="d-flex align-items-center justify-content-between pb-1 mb-2"
            style={{ borderBottom: "2px solid #dee2e6" }}
          >
            <h6 className="mb-0" style={{ fontSize: "0.9rem" }}>
              Products of <span style={{ color: "#f0a500" }}>{selectedBank.client_name}</span>
            </h6>
            <button
              className="custBtn"
              style={{ fontSize: "0.8rem", padding: "4px 12px" }}
              onClick={() => openProduct(null)}
              disabled={!uploadFields}
            >
              + Add Product
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered table-hover table-sm text-center" style={TABLE_STYLE}>
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>Sr No</th>
                  <th style={{ textAlign: "left" }}>Product</th>
                  <th style={{ width: "160px" }}>Excel format</th>
                  <th style={{ width: "170px" }}>Documents</th>
                  <th style={{ width: "90px" }}>Status</th>
                  <th style={{ width: PRODUCT_ACTIONS_WIDTH }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {bankProducts.map((p, index) => (
                  <tr key={p.Product_id} style={{ verticalAlign: "middle" }}>
                    <td>{index + 1}</td>
                    <td className="fw-semibold" style={{ textAlign: "left" }}>
                      {p.Product_name}
                    </td>
                    <td>
                      <FormatPill product={p} />
                    </td>
                    <td>{wordSummary && <WordStatus count={wordCount(p.Client_id, p.Product_id)} needs={wordSummary.productNeeds} />}</td>
                    <td>
                      <StatusPill status={p.Status} />
                    </td>
                    <td>
                      <div className="d-flex justify-content-center gap-1">
                        <ActionButton
                          variant={p.isConfigured ? "secondary" : "primary"}
                          onClick={() => openProduct(p)}
                          disabled={!uploadFields}
                        >
                          {p.isConfigured ? "Edit" : "Set up columns"}
                        </ActionButton>
                        <ActionButton
                          variant={p.Status === 0 ? "success" : "warning"}
                          onClick={() =>
                            setStatus(
                              `${API_BASE_URL}/api/products/${p.Product_id}/status`,
                              p.Status === 0,
                              p.Product_name,
                            )
                          }
                        >
                          {p.Status === 0 ? "Activate" : "Deactivate"}
                        </ActionButton>
                        <ActionButton
                          variant="danger"
                          onClick={() =>
                            handleDelete(
                              `${API_BASE_URL}/api/products/${p.Product_id}`,
                              p.Product_name,
                              " Only possible if no cases use this product.",
                            )
                          }
                        >
                          Delete
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
                {bankProducts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      No products yet. Add one to start uploading LOTs for this bank.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <BankFormModal
        show={bankModal.show}
        bank={bankModal.bank}
        onClose={() => setBankModal({ show: false, bank: null })}
        onSaved={(saved) => {
          setBankModal({ show: false, bank: null });
          setSelectedBankId(saved.Client_id);
          toastSuccess(`${saved.client_name} saved`);
          loadData();
        }}
      />

      <ProductFormModal
        show={productModal.show}
        bank={selectedBank}
        product={productModal.product}
        allProducts={products}
        banks={banks}
        uploadFields={uploadFields}
        onClose={() => setProductModal({ show: false, product: null })}
        onSaved={() => {
          setProductModal({ show: false, product: null });
          toastSuccess("Product saved");
          loadData();
        }}
      />

      <ToastContainer />
    </div>
  );
};

export default BanksProducts;
